import _ from "lodash";
import React, { useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { useTranslation } from "react-i18next";
import { allAndroidVersions, androidVersionOptions, products } from "src/data/products";
import { PermissionService } from "src/services/permissionService/permissionService";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { InputBaseProps } from "@mui/material";
import { Product } from "@products/types";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption, SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";

const customPlatformValue = "__custom__";

const getAndroidVersionOptions = (value: string | undefined) => {
    const versionNames = Object.keys(androidVersionOptions);
    const versions = value && versionNames.includes(value) ? androidVersionOptions[value] : allAndroidVersions;
    return versions.map((version) => ({
        label: version,
        value: version,
    }));
};

export const HardwareInput = () => {
    const { t } = useTranslation(["pages"]);
    const form = useForm<Product>();
    const { input: rootDeviceInput } = useField<string | undefined>("customBuildConfig.rootDevice");

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const customPlatformOption: SelectOption = useMemo(
        () => ({
            label: t("pages:productPage.input.hardwarePlatform.customPlatform"),
            value: customPlatformValue,
        }),
        [t],
    );

    const platformOptions: SelectOption[] = useMemo(
        () => [
            ..._(products)
                .map(({ deviceName, rootDevice }) => ({ label: deviceName, value: rootDevice }))
                .uniqBy("label")
                .sortBy("label")
                .value(),
            customPlatformOption,
        ],
        [customPlatformOption],
    );

    const isPredefinedOption = useMemo(
        () => products.some((product) => product.rootDevice === rootDeviceInput.value),
        [rootDeviceInput.value],
    );

    const selectedPlatform = useMemo(
        () =>
            isPredefinedOption ? platformOptions.find((option) => option.value === rootDeviceInput.value) : customPlatformOption,
        [isPredefinedOption, platformOptions, rootDeviceInput.value, customPlatformOption],
    );

    const onPlatformChange = (value: string) => {
        const isCustom = value === customPlatformValue;
        const rootDevice = isCustom ? "" : value;
        const androidVersions = getAndroidVersionOptions(rootDevice);
        const androidVersion = androidVersions[0].value;

        const change = form.change as (name: string, value: unknown) => void;
        form.batch(() => {
            change("customBuildConfig.rootDevice", rootDevice);
            form.change("androidVersion", androidVersion);
        });
    };
    const onChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }
        onPlatformChange(selectedOption.value);
    };
    const onBlur: InputBaseProps["onBlur"] = (e) => {
        if (products.some((p) => p.rootDevice === e.target.value)) {
            onPlatformChange(e.target.value);
        }
    };

    return (
        <React.Fragment>
            <SelectField
                label={t("pages:productPage.input.hardwarePlatform.caption")}
                value={selectedPlatform}
                options={platformOptions}
                onChange={onChange}
                fullWidth
                disabled={cannotManageProducts}
            />
            <TextField
                name="customBuildConfig.rootDevice"
                label={t("pages:productPage.input.hardwareCodename.caption")}
                placeholder={t("pages:productPage.input.hardwareCodename.caption")}
                onBlur={onBlur}
                fullWidth
                disabled={isPredefinedOption || cannotManageProducts}
            />
        </React.Fragment>
    );
};
